import axios from 'axios';
import {
  isPublicApiPath,
  isAuthMePath,
  isPublicAppRoute,
  isAnonymousAuthFlowRoute,
  clearStoredSession,
} from './apiAuth';
import { parseTierLimitError, notifyTierLimitError } from './tierErrors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

/**
 * Resolve a photo URL - handles both full URLs and storage paths
 */
export const resolvePhotoUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}/files/${url}`;
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Let the runtime set multipart boundaries; default JSON Content-Type breaks FormData uploads.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const h = config.headers;
    if (h) {
      if (typeof h.delete === 'function') {
        h.delete('Content-Type');
        h.delete('content-type');
      } else {
        delete h['Content-Type'];
        delete h['content-type'];
      }
    }
  }
  const reqUrl = config.url || '';
  if (isPublicApiPath(reqUrl)) {
    if (config.headers) {
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Authorization');
        config.headers.delete('authorization');
      } else {
        delete config.headers.Authorization;
        delete config.headers.authorization;
      }
    }
  } else {
    const token = localStorage.getItem('token');
    if (token) {
      if (!config.headers) config.headers = {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle auth errors — avoid nuking /tenant-setup when a stale token makes /auth/me fail
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const tierErr = parseTierLimitError(error);
    if (tierErr) {
      notifyTierLimitError(error);
    }

    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const reqUrl = error.config?.url || '';

    if (isPublicApiPath(reqUrl)) {
      return Promise.reject(error);
    }

    const fromAuthMe = isAuthMePath(reqUrl);
    const onPublicRoute = isPublicAppRoute() || isAnonymousAuthFlowRoute();

    if (fromAuthMe) {
      clearStoredSession();
      return Promise.reject(error);
    }

    if (onPublicRoute) {
      return Promise.reject(error);
    }

    clearStoredSession();

    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  },
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  verifyResetToken: (token) => api.get('/auth/reset-password/verify', { params: { token } }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
  resendVerification: () => api.post('/auth/resend-verification'),
};

// Properties API
export const propertiesAPI = {
  getAll: () => api.get('/properties'),
  getOne: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
  // Payment plan endpoints
  markPaymentPaid: (propertyId, paymentId) => api.put(`/properties/${propertyId}/payment-plan/${paymentId}/mark-paid`),
  addPaymentPlanItem: (propertyId, item) => api.post(`/properties/${propertyId}/payment-plan`, item),
  deletePaymentPlanItem: (propertyId, paymentId) => api.delete(`/properties/${propertyId}/payment-plan/${paymentId}`),
  // Mortgage monthly statements (stored in financing.loan_payments)
  listMortgageStatements: (propertyId) => api.get(`/properties/${propertyId}/mortgage-statements`),
  addMortgageStatement: (propertyId, item) => api.post(`/properties/${propertyId}/mortgage-statements`, item),
  updateMortgageStatement: (propertyId, statementId, item) =>
    api.put(`/properties/${propertyId}/mortgage-statements/${statementId}`, item),
  deleteMortgageStatement: (propertyId, statementId) =>
    api.delete(`/properties/${propertyId}/mortgage-statements/${statementId}`),
  markMortgageStatementPaid: (propertyId, statementId) =>
    api.put(`/properties/${propertyId}/mortgage-statements/${statementId}/mark-paid`),
};

// User preferences API
export const preferencesAPI = {
  update: (data) => api.put('/auth/preferences', data),
};

// Tenants API
export const tenantsAPI = {
  getAll: () => api.get('/tenants'),
  getOne: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
  resendInvitation: (id) => api.post(`/tenants/${id}/resend-invitation`),
  recordPayment: (id, amount, date) => api.post(`/tenants/${id}/payment?amount=${amount}&date=${date}`),
};

// Finances API
export const financesAPI = {
  getAll: (params) => api.get('/finances', { params }),
  create: (data) => api.post('/finances', data),
  update: (id, data) => api.put(`/finances/${id}`, data),
  markPaid: (id) => api.put(`/finances/${id}/pay`),
  delete: (id) => api.delete(`/finances/${id}`),
};

// Maintenance API
export const maintenanceAPI = {
  getAll: (params) => api.get('/maintenance', { params }),
  create: (data) => api.post('/maintenance', data),
  update: (id, params) => api.put(`/maintenance/${id}`, null, { params }),
  delete: (id) => api.delete(`/maintenance/${id}`),
};

// Meetings API
export const meetingsAPI = {
  getAll: (params) => api.get('/meetings', { params }),
  create: (data) => api.post('/meetings', data),
  update: (id, data) => api.put(`/meetings/${id}`, data),
  complete: (id, minutes) => api.put(`/meetings/${id}/complete?minutes=${encodeURIComponent(minutes)}`),
  delete: (id) => api.delete(`/meetings/${id}`),
  uploadMinutes: (id, formData) => api.post(`/meetings/${id}/upload-minutes`, formData, {
    timeout: 120000,
  }),
};

// Documents API
export const documentsAPI = {
  getAll: (params) => api.get('/documents', { params }),
  create: (data) => api.post('/documents', data),
  delete: (id) => api.delete(`/documents/${id}`),
  upload: (formData, onProgress) => api.post('/documents/upload', formData, {
    onUploadProgress: onProgress,
  }),
  analyze: (id) => api.post(`/documents/${id}/analyze`),
  analyzeInline: (formData) => api.post('/documents/analyze-inline', formData, {
    timeout: 120000,
  }),
  confirmExtraction: (id, body = {}) => api.post(`/documents/${id}/confirm-extraction`, body),
  applyLease: (id, body) => api.post(`/documents/${id}/apply-lease`, body),
  download: (id) => api.get(`/documents/${id}/download`, { responseType: 'blob' }),
};

// Notifications API
export const notificationsAPI = {
  sendPaymentReminder: (financeId) => api.post(`/notifications/payment-reminder?finance_id=${financeId}`),
  sendActionItems: (documentId) => api.post(`/notifications/action-items?document_id=${documentId}`),
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
  getHistory: (limit = 50) => api.get(`/notifications/history?limit=${limit}`),
  getInApp: (unreadOnly = false) => api.get(`/notifications/in-app?unread_only=${unreadOnly}`),
  markRead: (id) => api.put(`/notifications/in-app/${id}/read`),
  markAllRead: () => api.put('/notifications/in-app/read-all'),
  dismiss: (id) => api.delete(`/notifications/in-app/${id}`),
  clearAllInApp: () => api.delete('/notifications/in-app'),
  getStatus: () => api.get('/notifications/status'),
  sendTestEmail: () => api.post('/notifications/test-email'),
};

// Health API
export const healthAPI = {
  check: () => api.get('/health'),
};

// Exchange rates (TRM + cross-rates)
export const exchangeRatesAPI = {
  getLatest: () => api.get('/exchange-rates'),
};

// Accounting reports
export const reportsAPI = {
  getCatalog: () => api.get('/reports/catalog'),
  getFree: (type, params = {}) => api.get(`/reports/free/${type}`, { params }),
  generateAi: (type, body = {}, onProgress) => {
    // Use SSE streaming for AI reports
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('token');
      const url = `${API_BASE}/reports/ai/${type}`;
      
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      }).then(response => {
        if (!response.ok) {
          return response.json().then(d => reject({ response: { data: d, status: response.status } }));
        }
        
        const contentType = response.headers.get('content-type') || '';
        
        // SSE streaming response
        if (contentType.includes('text/event-stream')) {
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          
          const processStream = () => {
            reader.read().then(({ done, value }) => {
              if (done) {
                reject({ response: { data: { detail: 'Stream ended without result' } } });
                return;
              }
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || '';
              
              let eventType = '';
              for (const line of lines) {
                if (line.startsWith('event: ')) {
                  eventType = line.slice(7).trim();
                } else if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    if (eventType === 'progress' && onProgress) {
                      onProgress(data);
                    } else if (eventType === 'complete') {
                      resolve({ data });
                      return;
                    } else if (eventType === 'error') {
                      reject({ response: { data: { detail: data.message } } });
                      return;
                    }
                  } catch {}
                }
              }
              processStream();
            }).catch(reject);
          };
          processStream();
        } else {
          // Fallback: regular JSON response
          response.json().then(data => resolve({ data })).catch(reject);
        }
      }).catch(reject);
    });
  },
  generateDebtPrepayment: (body) => api.post('/reports/paid/debt_prepayment', body),
};

// Financial Dashboard API
export const financialAPI = {
  getPortfolioSummary: () => api.get('/financial/portfolio-summary'),
  getPropertySummary: (propertyId) => api.get(`/financial/${propertyId}/summary`),
  updateEstimatedValue: (propertyId, value) => api.put(`/properties/${propertyId}/estimated-value?value=${value}`),
  updateFinancing: (propertyId, data) => api.put(`/properties/${propertyId}/financing`, data),
  saveFinancingFromDocument: (propertyId, data) => api.put(`/properties/${propertyId}/financing-from-document`, data),
  markPaymentPaid: (propertyId, itemId) => api.put(`/properties/${propertyId}/payment-plan/${itemId}/pay`),
};

// Property Image API
export const propertyImageAPI = {
  upload: (propertyId, formData) => api.post(`/properties/${propertyId}/images`, formData, {
    timeout: 120000,
  }),
  delete: (propertyId, imageIndex) => api.delete(`/properties/${propertyId}/images/${imageIndex}`),
};

// AI Insights API
export const aiInsightsAPI = {
  generate: (propertyId) => api.get(`/properties/${propertyId}/ai-insights`, { timeout: 60000 }),
  getCached: (propertyId) => api.get(`/properties/${propertyId}/ai-insights/cached`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
};

// Tenant Portal API
export const tenantPortalAPI = {
  getDashboard: () => api.get('/tenant-portal/dashboard'),
  createMaintenanceRequest: (data) => api.post('/tenant-portal/maintenance', data),
  getNotifications: () => api.get('/tenant-portal/notifications'),
  markNotificationRead: (id) => api.put(`/tenant-portal/notifications/${id}/read`),
};

// Inspections API
export const inspectionsAPI = {
  getAll: (propertyId) => api.get(`/inspections${propertyId ? `?property_id=${propertyId}` : ''}`),
  create: (data) => api.post('/inspections', data),
  update: (id, data) => api.put(`/inspections/${id}`, data),
  delete: (id) => api.delete(`/inspections/${id}`),
};

// Seed Demo Data
export const seedDemoData = () => api.post('/seed-demo-data');

// Billing / subscription API
export const billingAPI = {
  getTiers: () => api.get('/billing/tiers'),
  getStatus: () => api.get('/billing/status'),
  getSubscription: () => api.get('/billing/subscription'),
  createCheckout: (body) => api.post('/billing/create-checkout-session', body),
  getPortal: () => api.get('/billing/portal'),
};

// Platform admin API
export const adminAPI = {
  getOverview: () => api.get('/admin/overview'),
  listUsers: (params) => api.get('/admin/users', { params }),
  updateUser: (userId, body) => api.patch(`/admin/users/${userId}`, body),
  setUserTier: (userId, body) => api.put(`/admin/users/${userId}/tier`, body),
  listNotifications: (params) => api.get('/admin/notifications', { params }),
  listAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  triggerReminderScan: () => api.post('/admin/notifications/trigger-scan'),
  getSecurityStatus: () => api.get('/security/status'),
};

export default api;
